import React, { useState, useEffect } from 'react';
import {
	Card,
	CardBody,
	CardHeader,
} from '../../../../_metronic/_partials/controls';
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Checkbox
} from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import {
	deleteTrainerCertification,
	getTrainerCertificationById,
	postTrainerCertification,
	updateTrainerCertification,
} from '../../../../api/trainerCertification';
import { useSkeleton } from '../../../hooks/useSkeleton';
import { alertError, alertSuccess } from '../../../../utils/logger';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';
import { shallowEqual, useSelector } from 'react-redux';
import MultilanguageTabBlock from '../../../components/MultilanguageTabBlock';
import { checkIsEmpty } from '../../../../utils/helpers';

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: '#F64E60',
		},
	},
});

function getEmptyTrainerCertification() {
	return {
		fullName: {},
		active: true
	};
}

export default function EditTrainerCertificationsPage() {
	const [trainerCertification, setTrainerCertification] = useState(getEmptyTrainerCertification());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const trainerCertificationId = useParams().id;
	const history = useHistory();
	const user = useSelector((store) => store.authentication?.user, shallowEqual)

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!trainerCertificationId) {
			disableLoadingData();
			return;
		}
		getTrainerCertificationById(trainerCertificationId)
			.then((res) => {
				if (res.status === 200) {
					setTrainerCertification(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({ error: error, customMessage: 'Could not get trainer certification.' });
				history.push('/trainer-certifications');
			});
	}, [trainerCertificationId, disableLoadingData, history]);

	function saveTrainerCertification() {
		if (checkIsEmpty(trainerCertification.fullName)) return alertError({ error: null, customMessage: 'The name is required in at least one of the languages.' })
		if (!trainerCertificationId) {
			postTrainerCertification(trainerCertification)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Trainer certification successfully created.',
						});
						history.push('/trainer-certifications');
					}
				})
				.catch((error) => {
					alertError({ error: error, customMessage: 'Could not save trainer certification.' });
				});
		} else {
			updateTrainerCertification(trainerCertificationId, trainerCertification)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: 'Saved!',
							customMessage: 'Changes successfully saved.',
						});
						history.push('/trainer-certifications');
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: 'Could not save changes.',
					});
				});
		}
	}

	const handleChange = (element, lang) => (event) => {
		if (event.target.value === ' ') return
		if (lang) {
			if (!trainerCertification[element]) trainerCertification[element] = {}
			let newText = trainerCertification[element]
			newText[lang] = event.target.value
			setTrainerCertification({ ...trainerCertification, [element]: newText })
		} else setTrainerCertification({ ...trainerCertification, [element]: event.target.value });
	};

	const renderMultilanguageTabContent = (lang) => {
		return <>
			<br/>
			<TextField
				id={`fullName`}
				label='Full name'
				value={(trainerCertification.fullName && trainerCertification.fullName[lang]) || ''}
				onChange={handleChange('fullName', lang)}
				InputLabelProps={{
					shrink: true,
				}}
				margin='normal'
				variant='outlined'
				required
			/>
		</>
	}

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title='Edit trainer certification'></CardHeader>
					<CardBody>
						<MultilanguageTabBlock
							multilanguageTabContent = {renderMultilanguageTabContent}
						/>
						<br/>
						<FormControlLabel
							control={<Checkbox checked={trainerCertification.active} onChange={() => setTrainerCertification({ ...trainerCertification, active: !trainerCertification.active })} name="checkActive" />}
							label="Active"
						/>
					</CardBody>
				</Card>
				<div style={{ display: 'flex', flexDirection: 'row' }}>
					<Button
						onClick={() => history.push('/trainer-certifications')}
						variant='outlined'
						style={{ marginRight: '20px' }}>
						Back
					</Button>
					<Button
						onClick={() => saveTrainerCertification()}
						variant='outlined'
						color='primary'
						style={{ marginRight: '20px' }}>
						Save trainer certification
					</Button>
					{trainerCertificationId && user?.role.includes('admin') && (
						<>
							<MuiThemeProvider theme={theme}>
								<Button
									onClick={() => setOpenConfirmDialog(true)}
									variant='outlined'
									color='secondary'
									style={{ marginRight: '20px' }}>
									Delete trainer certification
								</Button>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										marginLeft: 'auto',
									}}></div>
							</MuiThemeProvider>

							<ConfirmDialog
								title={'Are you sure you want to delete this trainer certification?'}
								open={openConfirmDialog}
								setOpen={setOpenConfirmDialog}
								onConfirm={() => {
									deleteTrainerCertification(trainerCertificationId)
										.then((res) => {
											if (res.status === 204 || res.status === 200) {
												alertSuccess({
													title: 'Deleted!',
													customMessage: 'Trainer certification deleted successfully',
												});
												history.push('/trainer-certifications');
											}
										})
										.catch((error) => {
											alertError({
												error: error,
												customMessage: 'Could not delete trainer certification.',
											});
										});
								}}
							/>
						</>
					)}
				</div>
			</>
		);
}
